const MM_TO_PT = 72 / 25.4;
const SLOT_COUNT = 12;
const TYPE_MAX = 15;

export const LABEL_WIDTH_MM = 55;
export const LABEL_HEIGHT_MM = 40;

const valueOrDash = (value) => {
  const name = String(value ?? "").trim();
  return name || "—";
};

const wrapLabeled = (label, value) => {
  const name = valueOrDash(value);
  const prefix = `${label}: `;
  if (name === "—" || name.length <= TYPE_MAX) {
    return [`${prefix}${name}`];
  }
  const spaceIdx = name.lastIndexOf(" ", TYPE_MAX);
  const cut = spaceIdx > 8 ? spaceIdx : TYPE_MAX;
  return [
    `${prefix}${name.slice(0, cut).trim()}`,
    name.slice(cut).trim().slice(0, TYPE_MAX),
  ].filter(Boolean);
};

const caseTypeValue = (item) => {
  const parent = item?.caseTypeName || item?.mainTypeName;
  if (parent) return parent;
  if (item?.mainTypeId) return "—";
  return item?.typeName;
};

const mobileValue = (item) => item?.mobileTypeName || item?.typeName;

const caseTypeLines = (item) => wrapLabeled("Case Type", caseTypeValue(item));

const mobileLines = (item) => wrapLabeled("Mobile", mobileValue(item));

const labelCaseStyle = (item) => {
  const raw = String(item?.caseStyleName || "").trim();
  if (!raw) return "Transparent";
  const split = raw.indexOf(" || ");
  if (split === -1) return raw;
  const english = raw.slice(split + 4).trim();
  const arabic = raw.slice(0, split).trim();
  return english || arabic || "Transparent";
};

const caseStyleLines = (item) => {
  const lines = [`CASE: ${labelCaseStyle(item)}`];
  if (item?.isPersonalized) lines.push("NAME");
  return lines;
};

export const buildSlotLabelLines = (item, batchNum, index) => {
  const slotNum = item?.machinePosition || index + 1;
  if (!item) {
    return [`Batch: ${batchNum}`, "EMPTY", `Slot: ${slotNum}/12`];
  }
  return [
    `Batch: ${batchNum}`,
    `Order: ${item.orderId}`,
    ...mobileLines(item),
    ...caseStyleLines(item),
    `Design: ${item.designCode || ""}`,
    `Item: ${item.itemIndexInOrder}/${item.totalItemsInOrder}`,
    `Slot: ${slotNum}/12`,
    ...caseTypeLines(item),
  ];
};

export const buildPendingLabelLines = (item) => {
  return [
    `Batch: ${item.batchNumber}`,
    `Order: ${item.orderId}`,
    ...mobileLines(item),
    ...caseStyleLines(item),
    `Design: ${item.designCode || ""}`,
    `Item: ${item.itemIndexInOrder}/${item.totalItemsInOrder}`,
    `Slot: ${item.machinePosition}/12`,
    ...caseTypeLines(item),
  ];
};

export const buildCustomBoardLabelLines = (item, slotNum) => {
  const lines = [`Batch: CUSTOM`];
  if (item?.orderId != null) lines.push(`Order: ${item.orderId}`);
  lines.push(
    `Design: ${item?.designCode || ""}`,
    `Item: ${item?.itemIndexInOrder}/${item?.totalItemsInOrder}`,
    `Slot: ${slotNum}/12`
  );
  return lines;
};

export const buildBatchSlotLineGroups = (slots, batchNum) =>
  Array.from({ length: SLOT_COUNT }, (_, index) =>
    buildSlotLabelLines(slots?.[index] ?? null, batchNum, index)
  );

export const buildTsplFromLines = (lines, startY = 28, lineH = 32, x = 40) => {
  let y = startY;
  const textCmds = (lines || [])
    .map((line) => {
      const safe = String(line).replace(/"/g, "'");
      const cmd = `TEXT ${x},${y},"3",0,1,1,"${safe}"`;
      y += lineH;
      return cmd;
    })
    .join("\n");

  return `
SIZE ${LABEL_WIDTH_MM} mm,${LABEL_HEIGHT_MM} mm
GAP 2 mm,0 mm
CLS
${textCmds}
PRINT 1
`;
};

const pdfEscape = (text) => {
  let out = "";
  for (const ch of String(text ?? "")) {
    const code = ch.charCodeAt(0);
    if (code === 0x5c) out += "\\\\";
    else if (code === 0x28) out += "\\(";
    else if (code === 0x29) out += "\\)";
    else if (code >= 32 && code <= 126) out += ch;
    else if (code >= 128 && code <= 255) {
      out += `\\${code.toString(8).padStart(3, "0")}`;
    } else {
      out += "?";
    }
  }
  return out;
};

const asciiBytes = (str) => {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) {
    bytes[i] = str.charCodeAt(i) & 0xff;
  }
  return bytes;
};

const pageContentStream = (lines) => {
  const pageH = LABEL_HEIGHT_MM * MM_TO_PT;
  const fontSize = 9;
  const left = 5 * MM_TO_PT;
  const baselineFromTop = 7 * MM_TO_PT;
  const lineH = 4.2 * MM_TO_PT;
  const startY = pageH - baselineFromTop;

  const ops = [
    "BT",
    `/F1 ${fontSize} Tf`,
    `${left.toFixed(2)} ${startY.toFixed(2)} Td`,
  ];

  (lines || []).forEach((line, i) => {
    if (i > 0) ops.push(`0 ${(-lineH).toFixed(2)} Td`);
    ops.push(`(${pdfEscape(line)}) Tj`);
  });
  ops.push("ET");
  return ops.join("\n");
};

const buildPdfBytes = (lineGroups) => {
  const pageW = (LABEL_WIDTH_MM * MM_TO_PT).toFixed(2);
  const pageH = (LABEL_HEIGHT_MM * MM_TO_PT).toFixed(2);
  const chunks = ["%PDF-1.4\n"];
  const offsets = [0];

  const addObject = (body) => {
    offsets.push(chunks.reduce((sum, part) => sum + part.length, 0));
    chunks.push(`${offsets.length - 1} 0 obj\n${body}\nendobj\n`);
  };

  addObject("<< /Type /Catalog /Pages 2 0 R >>");

  const pageObjectIds = lineGroups.map((_, i) => 4 + i * 2);
  addObject(
    `<< /Type /Pages /Kids [${pageObjectIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] /Count ${lineGroups.length} >>`
  );
  addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"
  );

  lineGroups.forEach((lines) => {
    const stream = pageContentStream(lines);
    const pageId = offsets.length;
    const contentId = pageId + 1;
    addObject(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  const xrefOffset = chunks.reduce((sum, part) => sum + part.length, 0);
  let xref = `xref\n0 ${offsets.length}\n`;
  xref += "0000000000 65535 f\r\n";
  for (let i = 1; i < offsets.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, "0")} 00000 n\r\n`;
  }
  chunks.push(xref);
  chunks.push(
    `trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`
  );

  return asciiBytes(chunks.join(""));
};

const triggerDownload = (bytes, fileName) => {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const downloadLabelsPdf = (lineGroups, fileName) => {
  const pages = (Array.isArray(lineGroups) ? lineGroups : [])
    .map((lines) => (Array.isArray(lines) ? lines : []).map((l) => String(l ?? "")))
    .filter((lines) => lines.length > 0);

  if (pages.length === 0) {
    throw new Error("لا توجد ملصقات للتحميل");
  }

  triggerDownload(buildPdfBytes(pages), fileName);
  return pages.length;
};
